import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { WorkflowData } from '../workflow';
import { useWorkflow } from '../workflow/hooks';
import { useShipmentScopeReason } from './useShipmentScopeReason';

/**
 * Hook to handle form integration for ItemCard components
 * Provides form methods and auto-quantity setting functionality
 */
export const useItemFormIntegration = (
  itemId?: string,
  enableQuantityAutoSetting: boolean = false,
  remainingQuantity: number = 0,
) => {
  const { data } = useWorkflow();
  const formContext = useFormContext<WorkflowData>();

  // Only use form features if explicitly requested
  const watch = enableQuantityAutoSetting ? formContext.watch : null;
  const setValue = enableQuantityAutoSetting ? formContext.setValue : null;
  const control = enableQuantityAutoSetting ? formContext.control : undefined;

  // Extract tracking number for stable dependency
  const trackingNumber = itemId
    ? data?.items?.[itemId]?.itemSelection?.trackingNumber
    : undefined;

  // Watch reason to detect when shipment scope reasons are selected
  const reasonId =
    itemId && watch ? watch(`items.${itemId}.reason.reasonId`) : undefined;

  // Get shipment scope reason info
  const { isShipmentScopeReason } = useShipmentScopeReason(itemId, reasonId);

  // Auto-set quantity to maximum when shipment scope reason is selected
  useEffect(() => {
    if (itemId && isShipmentScopeReason && remainingQuantity > 1 && setValue) {
      setValue(`items.${itemId}.itemSelection.quantity`, remainingQuantity);
    }
  }, [itemId, isShipmentScopeReason, remainingQuantity, setValue]);

  return {
    watch,
    setValue,
    control,
    trackingNumber,
    reasonId,
    isShipmentScopeReason,
  };
};
